import { UseCase, getEnv } from '@smile-next/uikit';
import { LoginMethod } from '@/constants';
import { CloudIdEmptyError } from '@/errors';
import { ErrorBase } from '@/errors/ErrorBase';
import { Auth, cognitoAuth } from '@/infra/auth/Auth';
import { SignApi, signApi } from '@/infra/auth/SignApi';
import { authParamsRepository, AuthParamsRepository } from '@/infra/repository/AuthParamsRepository';
import { AuthParamsConverter } from '@/models/domain/AuthParams';
import { CloudId } from '@/models/domain/CloudId';
import { LoginId } from '@/models/domain/LoginId';
import { Password } from '@/models/domain/Password';
import { AuthenticatedFailurePayload } from '@/usecases/payload/AuthenticatedFailurePayload';
import { UpdatedNextAccountLoginFormPayload } from '@/usecases/payload/UpdatedNextAccountLoginFormPayload';

type LoginNextAccountUsecaseInfraArgs = {
    authApi?: Auth;
    signApi?: SignApi;
    authParamsRepository?: AuthParamsRepository;
};

type LoginNextAccountUsecaseArgs = LoginNextAccountUsecaseInfraArgs & {
    skipFetchSignedCookie?: boolean;
};

type LoginNextAccountUsecaseProps = {
    cloudId?: CloudId;
    loginId?: LoginId;
    password?: Password;
    isDisplayedCloudId?: boolean;
};

export class LoginNextAccountUseCase extends UseCase {
    private infra: Required<LoginNextAccountUsecaseInfraArgs>;
    private skipFetchSignedCookie: boolean;

    constructor(args: LoginNextAccountUsecaseArgs = {}) {
        super();
        this.infra = {
            authApi: cognitoAuth,
            signApi,
            authParamsRepository,
            ...args,
        };
        this.skipFetchSignedCookie = args.skipFetchSignedCookie != null || getEnv().env === 'local';
    }

    async execute(props: LoginNextAccountUsecaseProps) {
        const { cloudId, loginId, password, isDisplayedCloudId } = props;
        const inputAuthParams = AuthParamsConverter.fromJSON({ cloudId: cloudId?.value });
        const savedAuthParams = this.infra.authParamsRepository.get();
        const authParams = savedAuthParams.join(inputAuthParams);

        if (!authParams.hasCloudId()) {
            // クラウドIDが指定されていない場合は失敗;
            this.dispatch(new AuthenticatedFailurePayload({ authParams, error: new CloudIdEmptyError() }));
            return Promise.resolve();
        }

        // 認証処理を実行する
        try {
            await this.infra.authApi.loginWithNextAccount(authParams.cloudId?.value, loginId?.value, password?.value);
            if (!this.skipFetchSignedCookie) {
                await this.infra.signApi.fetchSignedCookie();
            }
        } catch (error) {
            if (error instanceof ErrorBase) {
                this.dispatch(
                    new AuthenticatedFailurePayload({
                        authParams,
                        error,
                        isDisplayedCloudId,
                    }),
                );
            }
            return Promise.resolve();
        }

        // ログインメソッドでauthParamsを更新する
        const updatedAuthParams = authParams.updateLoginMethod(LoginMethod.NEXT_ACCOUNT);

        // 次回のログイン用の情報を保持する
        await this.infra.authParamsRepository.save(updatedAuthParams);

        await this.dispatch(
            new UpdatedNextAccountLoginFormPayload({
                authParams: updatedAuthParams,
                loginId,
                password,
            }),
        );

        return Promise.resolve();
    }
}
